/**
 * Screenshare Layout for Egress Recorder
 *
 * This component mirrors the screenshare layout from the main app's
 * LiveStreamVideoLayout.tsx. It measures the actual container dimensions
 * to properly calculate layout, supporting both egress and local testing.
 */

import { isTrackReference, TrackReference, VideoTrack } from '@livekit/components-react';
import { useEffect, useRef, useState } from 'react';
import ParticipantTileWithFallback from './ParticipantTileWithFallback';

// Layout constants scaled 1.5x for 1920x1080 egress resolution
// (main app uses 132/24/24 at 1280x720)
const TILE_SIZE = 198;
const TILE_GAP = 36;
const TILE_PADDING = 36;

interface ScreenshareLayoutResult {
  contentWidth: number;
  contentHeight: number;
  useOverlay: boolean;
  tilesDirection: 'vertical' | 'horizontal';
}

/**
 * Calculate the optimal layout for screenshare content and speaker tiles.
 * Copied from main app's screenshareLayout.ts utility.
 */
function calculateScreenshareLayout(
  containerWidth: number,
  containerHeight: number,
  contentRatio: number
): ScreenshareLayoutResult {
  // Step 1: Calculate content dimensions to fit within container
  let contentWidth = containerWidth;
  let contentHeight = containerWidth / contentRatio;

  // If too tall, fit to height instead
  if (contentHeight > containerHeight) {
    contentHeight = containerHeight;
    contentWidth = containerHeight * contentRatio;
  }

  // Step 2: Calculate remaining space in both directions
  const horizontalSpace = containerWidth - contentWidth;
  const verticalSpace = containerHeight - contentHeight;
  const tileSpaceNeeded = TILE_SIZE + TILE_PADDING * 2; // 132 + 48 = 180px

  // Step 3: Determine tile placement
  let useOverlay = false;
  let tilesDirection: 'vertical' | 'horizontal' = 'vertical';

  // Determine if container is portrait (taller than wide)
  const isPortraitContainer = containerHeight > containerWidth;

  if (horizontalSpace >= tileSpaceNeeded) {
    // Tiles to the RIGHT (vertical column) - tall content in wide container
    useOverlay = false;
    tilesDirection = 'vertical';
  } else if (verticalSpace >= tileSpaceNeeded && isPortraitContainer) {
    // Tiles BELOW only in portrait containers (tall containers)
    useOverlay = false;
    tilesDirection = 'horizontal';
  } else {
    // Overlay - tiles ON content (including wide content with letterbox in landscape)
    useOverlay = true;
    tilesDirection = isPortraitContainer ? 'vertical' : 'horizontal';
  }

  return {
    contentWidth,
    contentHeight,
    useOverlay,
    tilesDirection,
  };
}

interface ScreenshareLayoutProps {
  screenshareTrack: TrackReference;
  cameraTracks: TrackReference[];
}

export default function ScreenshareLayout({ screenshareTrack, cameraTracks }: ScreenshareLayoutProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const screenshareVideoRef = useRef<HTMLVideoElement>(null);
  const [screenshareRatio, setScreenshareRatio] = useState(16 / 9);
  const [containerSize, setContainerSize] = useState({ width: 1280, height: 720 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateSize = () => {
      setContainerSize({
        width: container.clientWidth,
        height: container.clientHeight,
      });
    };

    updateSize();
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  const containerWidth = containerSize.width;
  const containerHeight = containerSize.height;

  // Track screenshare video dimensions to calculate layout
  useEffect(() => {
    const video = screenshareVideoRef.current;
    if (!video) return;

    const updateRatio = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        setScreenshareRatio(video.videoWidth / video.videoHeight);
      }
    };

    video.addEventListener('loadedmetadata', updateRatio);
    video.addEventListener('resize', updateRatio);
    if (video.readyState >= 1) updateRatio();

    return () => {
      video.removeEventListener('loadedmetadata', updateRatio);
      video.removeEventListener('resize', updateRatio);
    };
  }, [screenshareTrack]);

  // Calculate layout at exact egress dimensions (no scaling needed)
  const layout = calculateScreenshareLayout(containerWidth, containerHeight, screenshareRatio);

  const isScreenShareAvailable =
    isTrackReference(screenshareTrack) && !!screenshareTrack.publication.track && !screenshareTrack.publication.isMuted;

  // Build wrapper class names
  const wrapperClasses = [
    'content-tiles-wrapper',
    layout.useOverlay && 'overlay-mode',
    !layout.useOverlay && layout.tilesDirection === 'horizontal' && 'landscape-side',
    !layout.useOverlay && layout.tilesDirection === 'vertical' && 'portrait-side',
  ]
    .filter(Boolean)
    .join(' ');

  // Build tiles class names
  const tilesClasses = [
    'speaker-tiles',
    layout.tilesDirection === 'vertical' && 'vertical',
    layout.tilesDirection === 'horizontal' && 'horizontal',
    layout.useOverlay && 'overlay',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="screenshare-container" ref={containerRef}>
      <div className={wrapperClasses}>
        {/* Screenshare content */}
        <div
          className="screenshare-content"
          style={{
            width: layout.contentWidth,
            height: layout.contentHeight,
          }}
        >
          {isScreenShareAvailable ? (
            <VideoTrack ref={screenshareVideoRef} trackRef={screenshareTrack} className="screenshare-video" />
          ) : (
            <div className="screenshare-placeholder">Screen share ended</div>
          )}
        </div>

        {/* Speaker tiles */}
        <div
          className={tilesClasses}
          style={{
            gap: TILE_GAP,
            padding: TILE_PADDING,
          }}
        >
          {cameraTracks.map((trackRef) => (
            <div
              key={trackRef.participant.sid}
              className="speaker-tile"
              style={{ width: TILE_SIZE, height: TILE_SIZE }}
            >
              <ParticipantTileWithFallback trackRef={trackRef} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
